import { DocumentLocationViewModel, DocumentLocationModelKind } from "client/Api/Hub/hubSettings";
import {
	Button,
	DialogBackdropState,
	Typography
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useCallback } from "react";
import { never } from "client_legacy/ndaw/errors";
import { ItemPickerLocation } from "client/Features/Documents/ItemPicker/ItemPicker";
import { AlertManager, useUnmountedCancellation } from "client/Hooks";
import { DocumentStoreType } from "client/Api/Documents/common";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import {
	getBreadCrumbsContentByFolder,
	getViewAndDocumentItemModelForLocation
} from "client/Api/Documents/SharePoint/sharepointDocuments";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { getFolderNavigationStack, getWebDavModel } from "client/Api/Documents/WebDav/webDav";
import { createRootFolderFromView } from "client/Features/Documents/SharePoint/SharePointStore";
import { RootFolder } from "client_legacy/Documents/SharePoint/models";
import {
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { SelectedLocation } from "./SelectedLocation";
import { DocumentLocationFormData } from "./HubSettings";
import styles from "./DocumentLocationSelector.module.less";
import { SelectDocumentsFolderDialogInitialData } from "client/Features/Documents/Dialogs/SelectDocumentsFolderDialog";

export type DocumentLocationSelectorProps = {
	attachable: IValueInputAttachable<OptionalModel<DocumentLocationFormData | null>>;
	alertManager: AlertManager;
	initialDefaultImageLocation: DocumentLocationFormData | null;
	onIsBusyChange: (isBusy: boolean) => void;
	onParentBackdropChange: (backdrop: DialogBackdropState) => void;
	onOpenSelectFolderDialog:
		(initialProps: SelectDocumentsFolderDialogInitialData) => Promise<TryResult<ItemPickerLocation>>;
};

export const DocumentLocationSelector = (props: DocumentLocationSelectorProps): ReactElement => {
	const {
		attachable,
		alertManager,
		initialDefaultImageLocation,
		onIsBusyChange,
		onOpenSelectFolderDialog,
		onParentBackdropChange
	} = props;

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const { value } = attachable.reducer;
	const documentLocation: DocumentLocationFormData | null = value.isSet
		? value.value
		: initialDefaultImageLocation;

	const changeHandler = useCallback(
		async (): Promise<void> => {
			let initialLocation: ItemPickerLocation | null = null;

			if (documentLocation) {
				initialLocation = await convertToItemPickerLocation(
					documentLocation,
					onIsBusyChange,
					globalCancellation
				);
			}

			onParentBackdropChange("invisible");

			const selectedLocationResult: TryResult<ItemPickerLocation> = await onOpenSelectFolderDialog({
				options: {
					allowRootFolder: true,
					allowSharePoint: true,
					allowWebDav: true
				},
				title: localize("_SelectDefaultFolder"),
				initialLocation: initialLocation ?? undefined
			});

			onParentBackdropChange("visible");

			if (!selectedLocationResult.wasSuccessful) {
				return;
			}

			const { result: selectedLocation } = selectedLocationResult;
			let displayName: string;

			try {
				displayName = await getDisplayName(
					selectedLocation,
					alertManager,
					globalCancellation,
					onIsBusyChange
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error("Calculation of display name failed", e);
				return;
			}

			const result: DocumentLocationFormData = convertToLocationFormModel(
				selectedLocationResult.result,
				displayName
			);

			attachable.reducer
				.setValue({
					isSet: true,
					value: result
				});

			attachable.validation
				.triggerInputFinished();

			attachable.validation
				.clearServerErrors();
		},
		[
			alertManager,
			attachable,
			globalCancellation,
			documentLocation,
			onIsBusyChange,
			onOpenSelectFolderDialog,
			onParentBackdropChange
		]
	);

	const removeHandler = useCallback(
		(): void => {
			attachable.reducer
				.setValue({
					isSet: true,
					value: null
				});

			attachable.validation
				.triggerInputFinished();

			attachable.validation
				.clearServerErrors();
		},
		[
			attachable.reducer,
			attachable.validation
		]
	);

	const trackingId: string = attachable
		.attachToTextInput()
		.trackingId;

	return (

		<div
			{...setTrackingId(trackingId)}
			className={styles.container}
		>
			{isNotNil(documentLocation)
				? (
					<SelectedLocation
						documentLocation={documentLocation}
						onChange={changeHandler}
						onRemove={removeHandler}
					/>
				)
				: (
					<Button
						kind="secondary"
						text={localize("_SelectAFolder")}
						onClick={changeHandler}
						trackingId="select-a-folder"
					/>
				)
			}

			<Typography
				isItalic
				textColor="secondary"
			>
				{localize("_SelectTheFolderThatOpensByDefaultWhenAddingImagesInfo")}
			</Typography>
		</div>
	);
};
DocumentLocationSelector.displayName = "DocumentLocationSelector";

export async function convertToItemPickerLocation(
	location: DocumentLocationFormData | DocumentLocationViewModel,
	onIsBusyChange: (isBusy: boolean) => void,
	signal: AbortSignal
): Promise<ItemPickerLocation | null> {
	switch (location.kind) {
		case DocumentLocationModelKind.SharePoint:
			try {
				onIsBusyChange(true);

				const {
					view,
					documentItem
				} = await getViewAndDocumentItemModelForLocation(
					location.listLocation,
					location.itemId,
					signal
				);

				let sharePointLocation: ItemPickerLocation;

				if (!documentItem) {
					sharePointLocation = {
						kind: DocumentStoreType.SharePoint,
						folder: {
							...createRootFolderFromView(view),
							view
						}
					};
				} else {
					if (documentItem.itemType !== DocumentItemType.Folder) {
						throw new Error("Document location must be a folder");
					}

					sharePointLocation = {
						kind: DocumentStoreType.SharePoint,
						folder: {
							...documentItem,
							isRoot: false,
							view
						}
					};
				}

				onIsBusyChange(false);
				return sharePointLocation;
			} catch (e) {
				if (isCancelledError(e)) {
					throw e;
				}

				onIsBusyChange(false);
				return null;
			}
		case DocumentLocationModelKind.WebDav:
			try {
				onIsBusyChange(true);

				const {
					item,
					server
				} = await getWebDavModel(
					location.serverId,
					location.fileId,
					signal
				);

				if (!item.isFolder) {
					throw new Error("Document location must be a folder");
				}

				onIsBusyChange(false);

				return {
					kind: DocumentStoreType.WebDav,
					folder: {
						id: item.id,
						name: item.name,
						server,
						parentId: item.parentId
					}
				};
			} catch (e) {
				if (isCancelledError(e)) {
					throw e;
				}

				onIsBusyChange(false);
				return null;
			}
		default:
			never(location);
	}
}

function convertToLocationFormModel(
	itemPickerLocation: ItemPickerLocation,
	displayName: string
): DocumentLocationFormData {
	const {
		kind,
		folder
	} = itemPickerLocation;

	switch (kind) {
		case DocumentStoreType.SharePoint:
			return {
				kind: DocumentLocationModelKind.SharePoint,
				displayName,
				itemId: folder.id,
				listLocation: folder.location,
			};
		case DocumentStoreType.WebDav:
			const {
				server
			} = folder;

			return {
				kind: DocumentLocationModelKind.WebDav,
				displayName,
				fileId: folder.id,
				serverId: server.id
			};
		default:
			never(kind);
	}
}

async function getDisplayName(
	location: ItemPickerLocation,
	alertManager: AlertManager,
	signal: AbortSignal,
	onIsBusyChange: (isBusy: boolean) => void
): Promise<string> {
	const {
		kind,
		folder
	} = location;

	let displayName: string = "";

	switch (kind) {
		case DocumentStoreType.SharePoint:
			const sharePointRootFolder: RootFolder = createRootFolderFromView(folder.view);
			displayName += sharePointRootFolder.name;

			if (folder.isRoot) {
				return displayName;
			}

 			try {
				onIsBusyChange(true);
				const sharePointBreadCrumbs = await getBreadCrumbsContentByFolder(
					folder.id,
					folder.location,
					signal
				);

				for (const x of sharePointBreadCrumbs) {
					displayName += `/${x.name}`;
				}

				displayName += `/${folder.name}`;

				onIsBusyChange(false);
			} catch (e) {
				if (isCancelledError(e)) {
					throw e;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
				onIsBusyChange(false);
				throw e;
			}
			break;
		case DocumentStoreType.WebDav:
			const webDavRootFolder = folder
				.server
				.name;

			displayName += webDavRootFolder;

			if (isNil(folder.parentId)) {
				return displayName;
			}

			try {
				onIsBusyChange(true);
				const webdavBreadCrumbs = await getFolderNavigationStack(
					folder.server.id,
					folder.id,
					signal
				);

				for (const x of webdavBreadCrumbs) {
					displayName += `/${x.name}`;
				}

				onIsBusyChange(false);
			} catch (e) {
				if (isCancelledError(e)) {
					throw e;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
				onIsBusyChange(false);
				throw e;
			}
			break;
		default:
			never(kind);
	}

	return displayName;
}